import { useCollection } from '@cloudscape-design/collection-hooks';
import {
  Table,
  Box,
  SpaceBetween,
  Button,
  Header,
  Pagination,
  TextFilter,
  Alert,
} from '@cloudscape-design/components';
import { useNavigate } from 'react-router-dom';
import { ConfigurationObject } from '../services/configs-api';
import { useIsAdmin } from '@features/auth';

interface ConfigTableProps {
  items: ConfigurationObject[];
  isLoading: boolean;
  error: Error | null;
  onRetry: () => void;
  onDelete: (config: ConfigurationObject) => void;
  selectedItems?: ConfigurationObject[];
  onSelectionChange?: (items: ConfigurationObject[]) => void;
}

const PAGE_SIZE = 25;

/**
 * Cloudscape Table component for displaying configuration objects.
 * Supports sorting, filtering, pagination, and admin-only delete button.
 *
 * Requirements: 2.1, 2.2, 2.3, 2.4, 2.5, 2.6, 2.7, 5.1, 5.2
 */
export function ConfigTable({ items, isLoading, error, onRetry, onDelete, selectedItems, onSelectionChange }: ConfigTableProps) {
  const navigate = useNavigate();
  const { isAdmin } = useIsAdmin();

  const { items: pageItems, filteredItemsCount, collectionProps, filterProps, paginationProps } =
    useCollection(items, {
      filtering: {
        empty: (
          <Box textAlign="center" color="inherit">
            <b>No configurations</b>
            <Box padding={{ bottom: 's' }} variant="p" color="inherit">
              No configurations match the filter.
            </Box>
          </Box>
        ),
        noMatch: (
          <Box textAlign="center" color="inherit">
            <b>No match</b>
            <Box padding={{ bottom: 's' }} variant="p" color="inherit">
              No configurations match the filter criteria.
            </Box>
          </Box>
        ),
        filteringFunction: (item, filterText) => {
          const lower = filterText.toLowerCase();
          return (
            item.configName.toLowerCase().includes(lower) ||
            item.configType.toLowerCase().includes(lower)
          );
        },
      },
      pagination: { pageSize: PAGE_SIZE },
      sorting: {
        defaultState: { sortingColumn: { sortingField: 'lastModified' }, isDescending: true },
      },
    });

  if (error && !isLoading) {
    return (
      <Alert type="error" action={<Button onClick={onRetry}>Retry</Button>}>
        Unable to load configurations. Please retry.
      </Alert>
    );
  }

  return (
    <Table
      {...collectionProps}
      loading={isLoading}
      loadingText="Loading configurations..."
      items={pageItems}
      selectionType={onSelectionChange ? 'multi' : undefined}
      selectedItems={selectedItems ?? []}
      onSelectionChange={({ detail }) => onSelectionChange?.(detail.selectedItems as ConfigurationObject[])}
      onRowClick={({ detail }) => {
        navigate(`/configs/${detail.item.configId}`);
      }}
      columnDefinitions={[
        {
          id: 'configName',
          header: 'Name',
          cell: (item) => (
            <span>
              {item.configName}
              {item.deletionScheduledAt && (
                <span style={{ marginLeft: '8px', background: '#FEF3CD', color: '#856404', padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 600 }}>
                  Pending Deletion
                </span>
              )}
            </span>
          ),
          sortingField: 'configName',
          width: 250,
        },
        {
          id: 'configType',
          header: 'Type',
          cell: (item) => item.configType,
          sortingField: 'configType',
          width: 150,
        },
        {
          id: 'businessUnit',
          header: 'Business Unit',
          cell: (item) => item.businessUnit ? (
            <span style={{ background: '#E8F0FE', color: '#1A73E8', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 600 }}>
              {item.businessUnit}
            </span>
          ) : '—',
          sortingField: 'businessUnit',
          width: 130,
        },
        {
          id: 'version',
          header: 'Version',
          cell: (item) => item.version,
          sortingField: 'version',
          width: 100,
        },
        {
          id: 'lastModified',
          header: 'Last Modified',
          cell: (item) => new Date(item.lastModified).toLocaleString(),
          sortingField: 'lastModified',
          width: 200,
        },
        {
          id: 'lastModifiedBy',
          header: 'Modified By',
          cell: (item) => item.lastModifiedBy,
          sortingField: 'lastModifiedBy',
          width: 200,
        },
        ...(isAdmin
          ? [
              {
                id: 'actions',
                header: 'Actions',
                cell: (item: ConfigurationObject) => (
                  <SpaceBetween direction="horizontal" size="xs">
                    <Button
                      variant="inline-link"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/configs/${item.configId}/edit`);
                      }}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="inline-link"
                      onClick={(e) => {
                        e.stopPropagation();
                        // Duplicate: navigate to create with pre-populated state
                        navigate('/configs/new', {
                          state: {
                            duplicate: true,
                            source: item,
                          },
                        });
                      }}
                    >
                      Duplicate
                    </Button>
                    <Button
                      variant="inline-link"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(item);
                      }}
                    >
                      Delete
                    </Button>
                  </SpaceBetween>
                ),
                width: 220,
              },
            ]
          : [
              {
                id: 'actions',
                header: 'Actions',
                cell: (item: ConfigurationObject) => (
                  <SpaceBetween direction="horizontal" size="xs">
                    <Button
                      variant="inline-link"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/configs/${item.configId}/edit`);
                      }}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="inline-link"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate('/configs/new', {
                          state: { duplicate: true, source: item },
                        });
                      }}
                    >
                      Duplicate
                    </Button>
                  </SpaceBetween>
                ),
                width: 160,
              },
            ]),
      ]}
      header={
        <Header
          counter={`(${filteredItemsCount ?? items.length})`}
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => navigate('/configs/new?mode=bulk')}>Bulk upload</Button>
              <Button variant="primary" onClick={() => navigate('/configs/new')}>
                Create configuration
              </Button>
            </SpaceBetween>
          }
        >
          Configurations
        </Header>
      }
      filter={
        <TextFilter
          {...filterProps}
          filteringPlaceholder="Search by name or type..."
          filteringAriaLabel="Filter configurations"
        />
      }
      pagination={<Pagination {...paginationProps} />}
      empty={
        <Box textAlign="center" color="inherit" padding={{ top: 'xxxl' }}>
          <SpaceBetween size="m">
            <b>No configurations</b>
            <Box variant="p" color="inherit">
              You haven't created any configurations yet.
            </Box>
            <Button variant="primary" onClick={() => navigate('/configs/new')}>
              Create your first configuration
            </Button>
          </SpaceBetween>
        </Box>
      }
    />
  );
}
